import { ChevronRightIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Link } from 'react-router-dom';

export interface IRecordRowProps {
  /** Glyph before the label; the row sizes it to 18px. */
  icon?: React.ReactNode;
  label: string;
  /** Small interactive node after the label text, such as an info tooltip trigger. */
  labelAddon?: React.ReactNode;
  /** Makes the whole row navigate there and appends a chevron. */
  to?: string;
  /** The value, flush right: `text-md` semibold unless a cell sets its own. Gives way before the label when the row is narrow. */
  children: React.ReactNode;
  className?: string;
}

/**
 * One row of a record section's card (Figma "Transaction/Row"): a muted label
 * with an optional leading glyph, the value flush right, and, when `to` is set,
 * a hover surface with a trailing chevron. 64px tall with its own side padding,
 * since the section card carries none.
 *
 * The link is an overlay across the row rather than a wrapper, so a
 * `labelAddon` such as a tooltip button never nests inside the anchor.
 */
const RecordRow = ({ icon, label, labelAddon, to, children, className }: IRecordRowProps) => {
  return (
    <div
      className={cx(
        'relative flex h-16 items-center justify-between gap-4 px-4',
        to && 'hover:bg-utility-brand-25',
        className
      )}
    >
      {to && <Link to={to} aria-label={label} className="absolute inset-0" />}
      <div className="text-tertiary text-md flex shrink-0 items-center gap-1 font-medium leading-[22px]">
        {icon && <span className="size-4.5 flex shrink-0 items-center justify-center [&>svg]:size-full">{icon}</span>}
        <span className="truncate">{label}</span>
        {labelAddon && <span className="relative flex shrink-0 items-center">{labelAddon}</span>}
      </div>
      <div className="text-primary text-md flex min-w-0 flex-1 items-center justify-end text-right font-semibold leading-[22px]">
        {children}
        {to && <ChevronRightIcon aria-hidden="true" className="text-quaternary m-1.5 size-4 shrink-0" />}
      </div>
    </div>
  );
};

export default RecordRow;
